import React from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // DISPLAY INFO (what visitors see)
  const DISPLAY_EMAIL = 'info@nolahvacconnect.com';
  
  // ACTUAL REDIRECTS (where calls/emails go) - YOUR REAL INFO
  const REAL_EMAIL = 'moodyhomeservicesllc@gmail.com';

  const scrollToForm = () => {
    const element = document.getElementById('lead-form') || document.getElementById('city-lead-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If no form on current page, go to homepage
      window.location.href = '/#lead-form';
    }
  };

  const serviceLinks = [
    { name: 'AC Repair & Installation', href: '/schedule-ac-repair' },
    { name: 'Heating Services', href: '/schedule-heating-service' },
    { name: 'Duct Cleaning', href: '/request-duct-cleaning' },
    { name: 'Maintenance Plans', href: '/maintenance-plans' },
  ];

  const serviceAreas = [
    { name: 'New Orleans, LA', slug: 'new-orleans' },
    { name: 'Metairie, LA', slug: 'metairie' },
    { name: 'Kenner, LA', slug: 'kenner' },
    { name: 'Slidell, LA', slug: 'slidell' },
    { name: 'Chalmette, LA', slug: 'chalmette' },
    { name: 'Covington, LA', slug: 'covington' },
    { name: 'Mandeville, LA', slug: 'mandeville' },
    { name: 'Hammond, LA', slug: 'hammond' },
    { name: 'Gretna, LA', slug: 'gretna' },
    { name: 'Marrero, LA', slug: 'marrero' },
  ];

  const handleServiceClick = (href) => {
    if (isHomePage) {
      // On homepage, scroll to section
      const sectionId = href.replace('/#', '#');
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // On other pages, navigate to homepage with hash
      window.location.href = href;
    }
  };

  return (
    <footer
      data-testid="footer"
      className="bg-[#0F172A] text-white"
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-10">
          {/* Company Info */}
          <div>
          <a href="/" className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-[#0EA5E9] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <span
              className="text-xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              NOLA HVAC Connect
            </span>
          </a>
            <p className="text-slate-400 leading-relaxed mb-6">
              Connecting homeowners with trusted, licensed HVAC professionals
              in the Greater New Orleans area.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="#"
                data-testid="social-facebook"
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#0EA5E9] transition-colors duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                data-testid="social-twitter"
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#0EA5E9] transition-colors duration-300"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="#"
                data-testid="social-instagram"
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#0EA5E9] transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#"
                data-testid="social-linkedin"
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-[#0EA5E9] transition-colors duration-300"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3
              className="text-lg font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Services
            </h3>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-slate-400 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Homeowner Resources */}
          <div>
            <h3
              className="text-lg font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Homeowner Resources
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="/hvac-pricing-new-orleans" data-testid="footer-resource-pricing" className="text-slate-400 hover:text-white transition-colors duration-300">
                  HVAC Pricing Guide
                </a>
              </li>
              <li>
                <a href="/ac-replacement-cost-new-orleans" data-testid="footer-resource-replacement" className="text-slate-400 hover:text-white transition-colors duration-300">
                  AC Replacement Cost
                </a>
              </li>
              <li>
                <a href="/instant-hvac-estimate-new-orleans" data-testid="footer-resource-estimate" className="text-slate-400 hover:text-white transition-colors duration-300">
                  Instant HVAC Estimate
                </a>
              </li>
              <li>
                <a href="/check-ac-age" data-testid="footer-resource-ac-age" className="text-slate-400 hover:text-white transition-colors duration-300">
                  How Old Is My AC?
                </a>
              </li>
              <li>
                <a href="/ac-unit-lifespan-louisiana" data-testid="footer-resource-lifespan" className="text-slate-400 hover:text-white transition-colors duration-300">
                  AC Lifespan in Louisiana
                </a>
              </li>
              <li>
                <a href="/repair-vs-replace-ac" data-testid="footer-resource-repair-replace" className="text-slate-400 hover:text-white transition-colors duration-300">
                  Repair vs. Replace
                </a>
              </li>
              <li>
                <a href="/hvac-refrigerant-guide" data-testid="footer-resource-refrigerant" className="text-slate-400 hover:text-white transition-colors duration-300">
                  Refrigerant Guide
                </a>
              </li>
              <li>
                <a href="/compare-hvac-contractors" data-testid="footer-resource-compare" className="text-slate-400 hover:text-white transition-colors duration-300">
                  Compare Contractors
                </a>
              </li>
              <li>
                <a href="/how-nola-hvac-connect-works" data-testid="footer-resource-how-it-works" className="text-slate-400 hover:text-white transition-colors duration-300">
                  How NOLA HVAC Connect Works
                </a>
              </li>
            </ul>
          </div>

          {/* Brand Guides */}
          <div>
            <h3
              className="text-lg font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Brand Guides
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="/who-makes-hvac-systems" className="text-white font-medium hover:text-[#0EA5E9] transition-colors duration-300">
                  Who Makes HVAC Systems?
                </a>
              </li>
              <li>
                <a href="/daikin-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">Daikin</a>
              </li>
              <li>
                <a href="/goodman-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">Goodman</a>
              </li>
              <li>
                <a href="/trane-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">Trane</a>
              </li>
              <li>
                <a href="/carrier-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">Carrier</a>
              </li>
              <li>
                <a href="/rheem-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">Rheem</a>
              </li>
              <li>
                <a href="/york-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">York</a>
              </li>
              <li>
                <a href="/american-standard-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">American Standard</a>
              </li>
              <li>
                <a href="/bryant-ac-problems" className="text-slate-400 hover:text-white transition-colors duration-300">Bryant</a>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3
              className="text-lg font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Service Areas
            </h3>
            <ul className="space-y-3">
              {serviceAreas.map((area, index) => (
                <li key={index}>
                  <a
                    href={`/hvac/${area.slug}`}
                    data-testid={`footer-area-${index}`}
                    className="text-slate-400 hover:text-white transition-colors duration-300"
                  >
                    {area.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3
              className="text-lg font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`mailto:${REAL_EMAIL}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors duration-300"
                >
                  <Mail className="w-5 h-5 text-[#0EA5E9]" />
                  <span>{DISPLAY_EMAIL}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" />
                <span>New Orleans, LA</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Clock className="w-5 h-5 text-[#0EA5E9]" />
                <span>24/7 Service Available</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Contractor CTA */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-12 pt-8 border-t border-slate-800">
          <div className="text-center">
            <p className="text-slate-400 mb-2">Are you an HVAC professional?</p>
            <a 
              href="/contractors"
              className="text-[#0EA5E9] hover:text-white font-medium transition-colors"
            >
              Become a certified contractor →
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              &copy; {currentYear} Moody Home Services LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a
                href="/sitemap.xml"
                className="text-slate-500 text-sm hover:text-white transition-colors"
              >
                Sitemap
              </a>
            </div>
          </div>
          <p className="text-slate-600 text-xs text-center mt-4">
            We connect homeowners with independent, licensed HVAC contractors in your area.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
